/** Serialized, durable field edits. The caller supplies a fully scoped identity key. */
export type AutosaveValues = Record<string, unknown>;
export type AutosaveStatus = "saved" | "pending" | "saving" | "error" | "conflict";
export interface AutosaveOptions {
  storageKey: string;
  values: AutosaveValues;
  enabled: boolean;
  save: (changes: AutosaveValues, expectedValues: AutosaveValues) => Promise<void>;
  delayMs?: number;
}
export interface AutosaveSnapshot {
  ready: boolean;
  values: AutosaveValues;
  dirty: boolean;
  status: AutosaveStatus;
  error: unknown;
  storageError: string | null;
}
type Edit = { expected: unknown; desired: unknown };
type Edits = Record<string, Edit>;
type Envelope = { version: 1; edits: Edits; attempt: Edits | null; acknowledged: AutosaveValues };
const PREFIX = "platform:autosave:v1:";
const TAB_KEY = "platform:autosave:tab:v1";
const own = (object: object, key: string) => Object.prototype.hasOwnProperty.call(object, key);
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
type TabIdentity = { id: string; durable: boolean; error: string | null };
let pageIdentity: Promise<TabIdentity> | null = null;
let releasePageLock: (() => void) | null = null;
let pageEventsInstalled = false;
let pageHidden = false;
const pageControllers = new Set<AutosaveController>();
const volatileIdentity = (error: string): TabIdentity => ({ id: crypto.randomUUID(), durable: false, error });

async function claimTabId(id: string): Promise<boolean> {
  const locks = window.navigator?.locks;
  if (!locks) return false;
  return new Promise<boolean>((resolveClaim) => {
    void locks.request(`${TAB_KEY}:${id}`, { ifAvailable: true }, async (lock) => {
      if (!lock) { resolveClaim(false); return; }
      resolveClaim(true);
      await new Promise<void>((resolveRelease) => { releasePageLock = resolveRelease; });
    }).catch(() => resolveClaim(false));
  });
}

async function acquirePageIdentity(): Promise<TabIdentity> {
  if (!window.navigator?.locks) return volatileIdentity("Web Locks are unavailable; this draft cannot survive reload safely.");
  let existing: string | null;
  try { existing = window.sessionStorage.getItem(TAB_KEY); }
  catch { return volatileIdentity("Session storage is unavailable; this draft cannot survive reload."); }
  if (existing && await claimTabId(existing)) return { id: existing, durable: true, error: null };
  // A duplicate/opened tab may inherit sessionStorage. Only the lock owner may
  // read or write that tab's localStorage envelope.
  for (let tries = 0; tries < 3; tries += 1) {
    const id = crypto.randomUUID();
    if (!await claimTabId(id)) continue;
    try {
      window.sessionStorage.setItem(TAB_KEY, id);
      return { id, durable: true, error: null };
    } catch {
      releasePageLock?.();
      releasePageLock = null;
      return volatileIdentity("Session storage is unavailable; this draft cannot survive reload.");
    }
  }
  return volatileIdentity("Tab identity could not be reserved; this draft cannot survive reload safely.");
}

function identity(): Promise<TabIdentity> {
  if (!pageEventsInstalled) {
    pageEventsInstalled = true;
    window.addEventListener("pagehide", () => {
      pageHidden = true;
      for (const controller of pageControllers) controller.pauseForPagehide();
      releasePageLock?.();
      releasePageLock = null;
      pageIdentity = null;
    });
    window.addEventListener("pageshow", () => {
      if (!pageHidden) return;
      pageHidden = false;
      for (const controller of pageControllers) controller.resumeAfterPageshow();
    });
  }
  return pageIdentity ??= acquirePageIdentity();
}

function validEdits(input: unknown): input is Edits {
  return !!input && typeof input === "object" && !Array.isArray(input)
    && Object.values(input).every((entry) => !!entry && typeof entry === "object" && own(entry, "expected") && own(entry, "desired"));
}

function statusOf(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

export class AutosaveController {
  private options: AutosaveOptions;
  private key = "";
  private ready = false;
  private durable = false;
  private readyPromise: Promise<void>;
  private server: AutosaveValues;
  private acknowledged: AutosaveValues = {};
  private edits: Edits = {};
  private attempt: Edits | null = null;
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight = false;
  private disposed = false;
  private active = false;
  private retries = 0;
  private retryDelay: number | null = null;
  private currentError: unknown = null;
  private currentStatus: AutosaveStatus = "saved";
  private currentStorageError: string | null = null;
  private storageLocked = false;
  private snapshot: AutosaveSnapshot;
  private readonly onOnline = () => { if (this.currentStatus === "error" && this.isRetryable(this.currentError)) this.retry(); };
  private readonly onBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!this.getSnapshot().dirty) return;
    event.preventDefault();
    event.returnValue = "";
  };

  constructor(options: AutosaveOptions, startOnCreate = true) {
    this.options = options;
    this.server = { ...options.values };
    this.snapshot = this.buildSnapshot();
    this.readyPromise = this.initialize();
    if (startOnCreate) this.activate();
  }

  private async initialize(): Promise<void> {
    const tab = await identity();
    if (this.disposed || pageHidden) return;
    this.key = `${PREFIX}${encodeURIComponent(tab.id)}:${encodeURIComponent(this.options.storageKey)}`;
    this.durable = tab.durable;
    this.currentStorageError = tab.error;
    if (tab.durable) {
      try {
        const raw = window.localStorage.getItem(this.key);
        if (raw) {
          const parsed: unknown = JSON.parse(raw);
          if (parsed && typeof parsed === "object") {
            const stored = parsed as Partial<Envelope>;
            if (stored.version === 1 && validEdits(stored.edits) && (stored.attempt === null || validEdits(stored.attempt))
              && stored.acknowledged && typeof stored.acknowledged === "object" && !Array.isArray(stored.acknowledged)) {
              if (!this.hasWork() && Object.keys(this.acknowledged).length === 0) {
                this.edits = stored.edits;
                this.attempt = stored.attempt ?? null;
                this.acknowledged = stored.acknowledged;
              }
            } else {
              this.storageLocked = true;
              this.currentStorageError = "Stored draft has an unknown format; existing browser data was left untouched.";
            }
          } else {
            this.storageLocked = true;
            this.currentStorageError = "Stored draft has an unknown format; existing browser data was left untouched.";
          }
        }
      } catch {
        this.storageLocked = true;
        this.currentStorageError = "Stored draft could not be read; existing browser data was left untouched.";
      }
    }
    this.currentStatus = this.hasWork() ? "pending" : "saved";
    this.ready = true;
    this.persist();
    this.emit();
    if (this.active && this.options.enabled && this.hasWork()) this.schedule(0);
  }

  whenReady = (): Promise<void> => this.readyPromise;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  getSnapshot = (): AutosaveSnapshot => this.snapshot;

  private hasWork(): boolean { return Object.keys(this.edits).length > 0 || this.attempt !== null; }
  private buildSnapshot(): AutosaveSnapshot {
    const values = { ...this.server, ...this.acknowledged };
    for (const [key, edit] of Object.entries(this.attempt ?? {})) values[key] = edit.desired;
    for (const [key, edit] of Object.entries(this.edits)) values[key] = edit.desired;
    return { ready: this.ready, values, dirty: this.hasWork(), status: this.currentStatus, error: this.currentError, storageError: this.currentStorageError };
  }
  private emit(): void {
    this.snapshot = this.buildSnapshot();
    for (const listener of this.listeners) listener();
  }
  private persist(): void {
    if (!this.ready || !this.durable || this.storageLocked) return;
    try {
      const envelope: Envelope = { version: 1, edits: this.edits, attempt: this.attempt, acknowledged: this.acknowledged };
      // Keep acknowledged values: a stale query after a successful save cannot roll the UI back.
      if (this.hasWork() || Object.keys(this.acknowledged).length) window.localStorage.setItem(this.key, JSON.stringify(envelope));
      else window.localStorage.removeItem(this.key);
      this.currentStorageError = null;
    } catch {
      this.currentStorageError = "Browser storage is unavailable or full; this draft may be lost on reload.";
    }
  }
  private schedule(delay: number): void {
    if (this.timer) clearTimeout(this.timer);
    if (!this.ready || !this.options.enabled || this.disposed || this.inFlight || !this.hasWork()) return;
    this.timer = setTimeout(() => { this.timer = null; void this.send(); }, delay);
  }
  private isRetryable(error: unknown): boolean {
    const status = statusOf(error);
    return status === undefined || status === 0 || status === 408 || status === 429 || status >= 500;
  }
  private async send(): Promise<void> {
    if (!this.ready || this.disposed || !this.options.enabled || this.inFlight || !this.hasWork()) return;
    if (!this.attempt) {
      this.attempt = copy(this.edits);
      this.edits = {};
      this.persist();
    }
    const attempt = this.attempt;
    const changes: AutosaveValues = {};
    const expected: AutosaveValues = {};
    for (const [key, edit] of Object.entries(attempt)) {
      changes[key] = edit.desired;
      expected[key] = edit.expected;
    }
    this.inFlight = true;
    this.currentStatus = "saving";
    this.currentError = null;
    this.emit();
    try {
      await this.options.save(changes, expected);
      if (this.disposed) return;
      for (const [key, edit] of Object.entries(attempt)) {
        this.acknowledged[key] = edit.desired;
        const newer = this.edits[key];
        if (newer) newer.expected = edit.desired;
      }
      this.attempt = null;
      this.retries = 0;
      this.currentStatus = this.hasWork() ? "pending" : "saved";
      this.persist();
      this.emit();
    } catch (error) {
      if (this.disposed) return;
      this.currentError = error;
      const status = statusOf(error);
      if (status === 400 || status === 422) {
        // Validation is a definite rejection. The attempted values are editable
        // again, and newer keystrokes keep the original server expectations.
        for (const [key, edit] of Object.entries(attempt)) {
          const newer = this.edits[key];
          this.edits[key] = { expected: edit.expected, desired: newer ? newer.desired : edit.desired };
        }
        this.attempt = null;
      }
      this.currentStatus = status === 409 || status === 412 ? "conflict" : "error";
      this.persist();
      this.emit();
      if (this.isRetryable(error) && this.retries < 3) {
        this.retries += 1;
        this.retryDelay = Math.min(1000 * 2 ** (this.retries - 1), 4000);
      }
    } finally {
      this.inFlight = false;
      if (!this.disposed && this.retryDelay !== null) {
        const delay = this.retryDelay;
        this.retryDelay = null;
        this.schedule(delay);
      } else if (!this.disposed && this.currentStatus === "pending") this.schedule(0);
    }
  }

  updateValues(values: AutosaveValues): void {
    this.server = { ...values };
    for (const [key, value] of Object.entries(this.acknowledged)) {
      if (own(values, key) && same(values[key], value)) delete this.acknowledged[key];
    }
    this.persist();
    this.emit();
  }
  /**
   * Use only with a read known to be newer than the last successful save (for
   * example a versioned, linearizable primary read). Ordinary query refreshes
   * may be stale and must go through updateValues instead.
   */
  acceptAuthoritativeValues(values: AutosaveValues): void {
    this.server = { ...values };
    for (const key of Object.keys(values)) delete this.acknowledged[key];
    this.persist();
    this.emit();
  }
  setEnabled(enabled: boolean): void {
    this.options = { ...this.options, enabled };
    if (!enabled && this.timer) { clearTimeout(this.timer); this.timer = null; }
    else if (enabled && this.hasWork() && this.currentStatus === "pending") this.schedule(0);
  }
  setSave(save: AutosaveOptions["save"]): void { this.options = { ...this.options, save }; }
  setValue = (key: string, value: unknown): void => {
    if (this.disposed) return;
    if (!this.ready) throw new Error("Autosave draft is not ready; wait for ready before editing.");
    // The transport and durable envelope are JSON; reject values that cannot round-trip.
    if (value === undefined || JSON.stringify(value) === undefined) throw new Error("Autosave values must be JSON serializable; use null for an empty field.");
    const current = this.getSnapshot().values[key];
    if (same(current, value)) return;
    const prior = this.edits[key];
    const active = this.attempt?.[key];
    const expected = prior ? prior.expected : active ? active.desired : (current === undefined ? null : current);
    if (same(value, expected) && !own(this.attempt ?? {}, key)) delete this.edits[key];
    else this.edits[key] = { expected, desired: value };
    const rejectedStatus = statusOf(this.currentError);
    const blocked = this.currentStatus === "conflict"
      || (this.currentStatus === "error" && !this.isRetryable(this.currentError) && rejectedStatus !== 400 && rejectedStatus !== 422);
    if (!blocked) this.currentError = null;
    if (!blocked && !this.inFlight) this.currentStatus = this.hasWork() ? "pending" : "saved";
    this.persist(); // synchronous, before a debounce can run
    this.emit();
    if (this.currentStatus === "pending") this.schedule(this.options.delayMs ?? 600);
  };
  retry = (): void => {
    if (this.disposed || !this.hasWork()) return;
    this.retries = 0;
    this.retryDelay = null;
    this.currentError = null;
    this.currentStatus = "pending";
    this.emit();
    this.schedule(0);
  };
  flush = (): void => {
    if (this.disposed || this.currentStatus === "conflict" || (this.currentStatus === "error" && !this.isRetryable(this.currentError))) return;
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    void this.send();
  };
  discard = (): void => {
    if (this.disposed) return;
    // An in-flight write has an unknown outcome; do not erase its recovery envelope.
    if (this.inFlight) return;
    this.edits = {};
    this.attempt = null;
    this.currentError = null;
    this.currentStatus = "saved";
    this.retries = 0;
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    this.persist();
    this.emit();
  };
  destroy(): void {
    this.disposed = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (this.active && typeof window !== "undefined") {
      window.removeEventListener("online", this.onOnline);
      window.removeEventListener("beforeunload", this.onBeforeUnload);
    }
    this.active = false;
    pageControllers.delete(this);
    this.listeners.clear();
  }
  /** React StrictMode replays effects with the same controller instance. */
  activate(): void {
    if (this.active) return;
    this.disposed = false;
    this.active = true;
    pageControllers.add(this);
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.onOnline);
      window.addEventListener("beforeunload", this.onBeforeUnload);
    }
    if (this.options.enabled && this.hasWork()) this.schedule(0);
  }
  pauseForPagehide(): void {
    this.ready = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.emit();
  }
  resumeAfterPageshow(): void {
    if (this.disposed) return;
    this.readyPromise = this.initialize();
  }
}

export function createAutosaveController(options: AutosaveOptions): AutosaveController {
  return new AutosaveController(options);
}
